import React, { useState } from "react";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

export default function AiAssistant() {
  const { post } = useApi();
  const [intent, setIntent] = useState("first_contact");
  const [channel, setChannel] = useState("email");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState("");

  const [objQ, setObjQ] = useState("");
  const [objA, setObjA] = useState("");
  const [objBusy, setObjBusy] = useState(false);

  const draft = async () => {
    setBusy(true);
    setOutput("");
    try {
      const r = await post("/ai/draft", { intent, channel, extra_context: extra });
      setOutput(r.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setBusy(false); }
  };

  const objection = async () => {
    setObjBusy(true);
    setObjA("");
    try {
      const r = await post("/ai/objection", { question: objQ });
      setObjA(r.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setObjBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Claude Sonnet 4.5</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          AI Drafting Assistant
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Generic drafts for templates and objection handling. For contact-specific drafts, use the
          AI panel inside a contact's profile — it'll pull in the property, surplus, and county automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Draft outreach</p>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-1">Intent</p>
              <Select value={intent} onValueChange={setIntent}>
                <SelectTrigger data-testid="ai-intent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_contact">First contact</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="claim_summary">Claim summary for claimant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-1">Channel</p>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="ai-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="mail">US Mail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-1">Extra context (optional)</p>
              <Textarea
                value={extra} onChange={(e) => setExtra(e.target.value)}
                placeholder="e.g., claimant is elderly and prefers phone follow-up"
                data-testid="ai-extra-input"
              />
            </div>
            <Button onClick={draft} disabled={busy} data-testid="ai-draft-btn" className="bg-slate-900 hover:bg-slate-800">
              <Sparkles className="h-4 w-4 mr-1.5" /> {busy ? "Drafting…" : "Draft"}
            </Button>
            {output && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Output</p>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }} data-testid="ai-copy-draft-btn">
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded text-sm whitespace-pre-wrap max-h-96 overflow-auto" data-testid="ai-draft-output">
                  {output}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Objection handling</p>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Paste a claimant's question or concern. Claude will draft a transparent, plain-language reply
              that respects their right to verify or file on their own.
            </p>
            <Textarea
              value={objQ} onChange={(e) => setObjQ(e.target.value)}
              placeholder={`e.g., "Is this a scam? I've never heard of surplus funds."`}
              data-testid="ai-objection-input"
              className="min-h-[100px]"
            />
            <Button onClick={objection} disabled={objBusy || !objQ.trim()} data-testid="ai-objection-btn" variant="outline">
              {objBusy ? "Thinking…" : "Draft response"}
            </Button>
            {objA && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm whitespace-pre-wrap" data-testid="ai-objection-output">
                {objA}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
